import React from 'react';

import { TestCycle } from '@qality/common-model';
import { TestCyclesService } from '@qality/common-service';

import { TestCyclesContext } from './TestCyclesContext';

export interface TestCyclesProviderProps {
  children: React.ReactNode;
  initLoading?: boolean;
}

export const TestCyclesProvider: React.FC<TestCyclesProviderProps> = ({
  children,
  initLoading,
}: TestCyclesProviderProps) => {
  const [selectedProjectIds, setSelectedProjectIds] = React.useState<string>();
  const [error, setError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(initLoading);
  const [testCycles, setTestCycles] = React.useState<TestCycle[]>([]);

  const [pagination, setPagination] = React.useState(null);

  let searchPromiseDate = null;

  const fetchTestCycles = async (
    projectIds: string | null = null,
    name = '',
    page = 0,
    versionId: string | null = null,
    isClosed: boolean | null = null
  ): Promise<Array<TestCycle>> => {
    const date = Date.now();
    const shouldFetchPermissions = true;
    const shouldFetchVersions = true;
    const elementsPerPage = 15;
    searchPromiseDate = date;

    setIsLoading(true);
    setError(null);

    let testCyclesResponse;

    try {
      testCyclesResponse = await TestCyclesService.fetchTestCycles(
        page,
        {
          ...(versionId ? { versionId } : null),
          ...(name ? { name } : null),
          ...(isClosed !== null ? { isClosed } : null),
          ...(projectIds !== null ? { projectIds } : null),
          includeStatuses: true,
        },
        shouldFetchPermissions,
        shouldFetchVersions,
        elementsPerPage
      );

      // In case some other newer promise started in the meantime, do nothing and abandon this response
      // Check if date is smaller -> it is older promise
      if (date < searchPromiseDate) {
        return [];
      }

      setSelectedProjectIds(projectIds);
      setTestCycles(testCyclesResponse.testCycles);
      setPagination({
        size: testCyclesResponse.pagination.pageSize,
        page: testCyclesResponse.pagination.currentPage + 1,
        total: testCyclesResponse.pagination.totalElements,
        pages: testCyclesResponse.pagination.totalPages,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e);
    }

    setIsLoading(false);
    return testCyclesResponse.testCycles;
  };

  return (
    <TestCyclesContext.Provider
      value={{
        projectIds: selectedProjectIds,
        isLoading,
        error,
        testCycles,
        fetchTestCycles,
        pagination,
      }}
    >
      {children}
    </TestCyclesContext.Provider>
  );
};
